import React, { useState, useMemo } from 'react';

function MonthlyCalendar({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDay, setHoveredDay] = useState(null);
    const [hoveredMonth, setHoveredMonth] = useState(null);
    const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'yearly'

    // Get the current month and year
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Format value based on display mode (for pre-calculated unit values)
    const formatValueUnits = (value, showPrefix = true) => {
        if (value === undefined || value === null) return '';
        const prefix = showPrefix ? (value >= 0 ? '+' : '-') : '';
        return `${prefix}${Math.abs(value).toFixed(1)}U`;
    };

    // Format value based on display mode (for dollar values that need conversion)
    const formatValue = (value, showPrefix = true) => {
        if (value === undefined || value === null) return '';
        if (displayMode === 'units') {
            const units = value / unitSize;
            const prefix = showPrefix ? (units >= 0 ? '+' : '-') : '';
            return `${prefix}${Math.abs(units).toFixed(1)}U`;
        }
        const prefix = showPrefix ? (value >= 0 ? '+$' : '-$') : '$';
        return `${prefix}${Math.abs(value).toFixed(2)}`;
    };

    const formatStaked = (value) => {
        if (displayMode === 'units') {
            const units = value / unitSize;
            return `${units.toFixed(1)}U`;
        }
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStakedUnits = (value) => {
        return `${value.toFixed(1)}U`;
    };

    // Calculate daily P/L data (with unit-based calculations when advanced mode is on)
    const dailyPLMap = useMemo(() => {
        const map = {};
        bets.forEach(bet => {
            const dateKey = bet.date;
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            
            if (!map[dateKey]) {
                map[dateKey] = { 
                    pl: 0, bets: 0, wins: 0, losses: 0, cashedOut: 0, pending: 0, staked: 0, returned: 0,
                    plUnits: 0, stakedUnits: 0, returnedUnits: 0
                };
            }
            map[dateKey].pl += bet.pl;
            map[dateKey].plUnits += bet.pl / betUnitSize;
            map[dateKey].bets++;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                map[dateKey].staked += bet.stake;
                map[dateKey].stakedUnits += bet.stake / betUnitSize;
            }
            
            // Only accumulate returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                map[dateKey].returned += (bet.bonus_bet ? 0 : bet.stake) + bet.pl;
                map[dateKey].returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
            
            if (bet.result === 'win') map[dateKey].wins++;
            if (bet.result === 'loss') map[dateKey].losses++;
            if (bet.result === 'cash out') map[dateKey].cashedOut++;
            if (bet.result === 'pending') map[dateKey].pending++;
        });
        // Calculate derived stats for each day
        Object.values(map).forEach(day => {
            day.winRate = (day.wins + day.losses) > 0 ? (day.wins / (day.wins + day.losses)) * 100 : 0;
            day.roi = day.staked > 0 ? (day.pl / day.staked) * 100 : 0;
        });
        return map;
    }, [bets, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate monthly P/L data (for yearly view)
    const monthlyPLMap = useMemo(() => {
        const map = {};
        bets.forEach(bet => {
            const betDate = new Date(bet.date);
            const monthKey = `${betDate.getFullYear()}-${String(betDate.getMonth() + 1).padStart(2, '0')}`;
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            
            if (!map[monthKey]) {
                map[monthKey] = { 
                    pl: 0, bets: 0, wins: 0, losses: 0, cashedOut: 0, pending: 0, staked: 0, returned: 0,
                    plUnits: 0, stakedUnits: 0, returnedUnits: 0
                };
            }
            map[monthKey].pl += bet.pl;
            map[monthKey].plUnits += bet.pl / betUnitSize;
            map[monthKey].bets++;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                map[monthKey].staked += bet.stake;
                map[monthKey].stakedUnits += bet.stake / betUnitSize;
            }
            
            // Only accumulate returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                map[monthKey].returned += (bet.bonus_bet ? 0 : bet.stake) + bet.pl;
                map[monthKey].returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
            
            if (bet.result === 'win') map[monthKey].wins++;
            if (bet.result === 'loss') map[monthKey].losses++;
            if (bet.result === 'cash out') map[monthKey].cashedOut++;
            if (bet.result === 'pending') map[monthKey].pending++;
        });
        // Calculate derived stats for each month
        Object.values(map).forEach(month => {
            month.winRate = (month.wins + month.losses) > 0 ? (month.wins / (month.wins + month.losses)) * 100 : 0;
            month.roi = month.staked > 0 ? (month.pl / month.staked) * 100 : 0;
        });
        return map;
    }, [bets, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate month stats (for daily view summary)
    const monthStats = useMemo(() => {
        const monthBets = bets.filter(bet => {
            const betDate = new Date(bet.date);
            return betDate.getMonth() === currentMonth && betDate.getFullYear() === currentYear;
        });

        const totalBets = monthBets.length;
        const wins = monthBets.filter(b => b.result === 'win').length;
        const losses = monthBets.filter(b => b.result === 'loss').length;
        const cashedOut = monthBets.filter(b => b.result === 'cash out').length;
        const pl = monthBets.reduce((sum, b) => sum + b.pl, 0);
        // Exclude bonus bets from staked totals
        const staked = monthBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake), 0);
        
        // Calculate returned only for settled bets (exclude pending)
        const settledBets = monthBets.filter(b => b.result !== 'pending');
        const returned = settledBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake) + b.pl, 0);

        // Calculate unit-based values
        let plUnits = 0, stakedUnits = 0, returnedUnits = 0;
        monthBets.forEach(bet => {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            plUnits += bet.pl / betUnitSize;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                stakedUnits += bet.stake / betUnitSize;
            }
            // Only count returned for settled bets
            if (bet.result !== 'pending') {
                returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalBets,
            wins,
            losses,
            cashedOut,
            pl,
            staked,
            returned,
            plUnits,
            stakedUnits,
            returnedUnits,
            roi: staked > 0 ? (pl / staked) * 100 : 0,
            winRate: (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0
        };
    }, [bets, currentMonth, currentYear, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Calculate year stats (for yearly view summary)
    const yearStats = useMemo(() => {
        const yearBets = bets.filter(bet => {
            const betDate = new Date(bet.date);
            return betDate.getFullYear() === currentYear;
        });

        const totalBets = yearBets.length;
        const wins = yearBets.filter(b => b.result === 'win').length;
        const losses = yearBets.filter(b => b.result === 'loss').length;
        const cashedOut = yearBets.filter(b => b.result === 'cash out').length;
        const pl = yearBets.reduce((sum, b) => sum + b.pl, 0);
        // Exclude bonus bets from staked totals
        const staked = yearBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake), 0);
        
        // Calculate returned only for settled bets (exclude pending)
        const settledBets = yearBets.filter(b => b.result !== 'pending');
        const returned = settledBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake) + b.pl, 0);

        // Calculate unit-based values
        let plUnits = 0, stakedUnits = 0, returnedUnits = 0;
        yearBets.forEach(bet => {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            plUnits += bet.pl / betUnitSize;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                stakedUnits += bet.stake / betUnitSize;
            }
            // Only count returned for settled bets
            if (bet.result !== 'pending') {
                returnedUnits += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / betUnitSize;
            }
        });

        return {
            totalBets,
            wins,
            losses,
            cashedOut,
            pl,
            staked,
            returned,
            plUnits,
            stakedUnits,
            returnedUnits,
            roi: staked > 0 ? (pl / staked) * 100 : 0,
            winRate: (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0
        };
    }, [bets, currentYear, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Generate yearly calendar months
    const yearlyMonths = useMemo(() => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthNames.map((name, index) => {
            const monthKey = `${currentYear}-${String(index + 1).padStart(2, '0')}`;
            const monthData = monthlyPLMap[monthKey];
            return {
                name,
                monthIndex: index,
                monthKey,
                ...monthData
            };
        });
    }, [currentYear, monthlyPLMap]);

    // Generate calendar days
    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the first of the month
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ day: null, isEmpty: true });
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayData = dailyPLMap[dateKey];
            
            days.push({
                day,
                dateKey,
                isEmpty: false,
                ...dayData
            });
        }

        return days;
    }, [currentMonth, currentYear, dailyPLMap]);

    // Navigate months
    const goToPrevMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    };

    // Navigate years
    const goToPrevYear = () => {
        setCurrentDate(new Date(currentYear - 1, currentMonth, 1));
    };

    const goToNextYear = () => {
        setCurrentDate(new Date(currentYear + 1, currentMonth, 1));
    };

    // Format for display - use unit values directly when in advanced mode
    const formatPL = (dollarValue, unitValue) => {
        if (dollarValue === undefined || dollarValue === null) return '';
        if (displayMode === 'units') {
            const value = advancedUnitSettings ? unitValue : dollarValue / unitSize;
            const prefix = value >= 0 ? '+' : '-';
            return `${prefix}${Math.abs(value).toFixed(1)}U`;
        }
        const prefix = dollarValue >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(dollarValue).toFixed(2)}`;
    };

    const formatStakedValue = (dollarValue, unitValue) => {
        if (displayMode === 'units') {
            const value = advancedUnitSettings ? unitValue : dollarValue / unitSize;
            return `${value.toFixed(1)}U`;
        }
        return `$${dollarValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatCurrency = (value) => {
        return formatValue(value, true);
    };

    const formatCurrencyFull = (value) => {
        return formatValue(value, true);
    };

    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // Find max absolute P/L for intensity scaling (daily)
    const maxAbsPL = Math.max(
        ...Object.values(dailyPLMap).map(d => Math.abs(d.pl)),
        1
    );

    // Find max absolute P/L for intensity scaling (monthly)
    const maxAbsMonthlyPL = Math.max(
        ...Object.values(monthlyPLMap).map(d => Math.abs(d.pl)),
        1
    );

    const getDayColor = (pl) => {
        if (pl === undefined) return 'bg-slate-800/30';
        
        const intensity = Math.min(Math.abs(pl) / maxAbsPL, 1);
        
        if (pl > 0) {
            if (intensity > 0.7) return 'bg-emerald-500/60';
            if (intensity > 0.4) return 'bg-emerald-500/40';
            return 'bg-emerald-500/20';
        } else if (pl < 0) {
            if (intensity > 0.7) return 'bg-red-500/60';
            if (intensity > 0.4) return 'bg-red-500/40';
            return 'bg-red-500/20';
        }
        return 'bg-slate-800/30';
    };

    const getMonthColor = (pl) => {
        if (pl === undefined) return 'bg-slate-800/30';
        
        const intensity = Math.min(Math.abs(pl) / maxAbsMonthlyPL, 1);
        
        if (pl > 0) {
            if (intensity > 0.7) return 'bg-emerald-500/60';
            if (intensity > 0.4) return 'bg-emerald-500/40';
            return 'bg-emerald-500/20';
        } else if (pl < 0) {
            if (intensity > 0.7) return 'bg-red-500/60';
            if (intensity > 0.4) return 'bg-red-500/40';
            return 'bg-red-500/20';
        }
        return 'bg-slate-800/30';
    };

    const currentStats = viewMode === 'daily' ? monthStats : yearStats;

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 p-3 md:p-6">
            {/* View Toggle */}
            <div className="flex justify-end mb-3 md:mb-4">
                <div className="flex bg-slate-800/50 rounded-xl p-1 border border-slate-700/50">
                    <button
                        onClick={() => setViewMode('daily')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                            viewMode === 'daily'
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        Daily
                    </button>
                    <button
                        onClick={() => setViewMode('yearly')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                            viewMode === 'yearly'
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        Monthly
                    </button>
                </div>
            </div>

            {viewMode === 'daily' ? (
                <>
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                        <button
                            onClick={goToPrevMonth}
                            className="p-1.5 md:p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                        >
                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        
                        <h4 className="text-base md:text-lg font-semibold text-white">{monthName}</h4>
                        
                        <button
                            onClick={goToNextMonth}
                            className="p-1.5 md:p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                        >
                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Day Headers - Hidden on mobile since we use a different layout */}
                    <div className="hidden md:grid grid-cols-7 gap-1 mb-1">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                            <div key={idx} className="text-center text-xs font-medium text-slate-500 py-2">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar Grid - 4 cols on mobile, 7 cols on desktop */}
                    <div className="grid grid-cols-4 md:grid-cols-7 gap-1 md:gap-1">
                        {calendarDays.map((day, index) => (
                            <div
                                key={index}
                                className={`relative aspect-square rounded-md md:rounded-lg transition-all duration-200 ${
                                    day.isEmpty 
                                        ? 'bg-transparent' 
                                        : `${getDayColor(day.pl)} hover:ring-1 hover:ring-slate-500/50`
                                } ${day.bets > 0 ? 'cursor-pointer' : ''}`}
                                onMouseEnter={() => day.bets > 0 && setHoveredDay(day)}
                                onMouseLeave={() => setHoveredDay(null)}
                            >
                                {!day.isEmpty && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center p-1 overflow-hidden">
                                        {/* Day number */}
                                        <span className={`text-xs md:text-sm font-medium leading-tight ${
                                            day.bets > 0 ? 'text-white' : 'text-slate-500'
                                        }`}>
                                            {day.day}
                                        </span>
                                        
                                        {/* P/L value */}
                                        {day.pl !== undefined && (
                                            <span className={`text-[10px] md:text-xs font-semibold leading-tight truncate max-w-full ${
                                                day.pl >= 0 ? 'text-emerald-300' : 'text-red-300'
                                            }`}>
                                                {formatPL(day.pl, day.plUnits)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                
                                {/* Hover Tooltip */}
                                {hoveredDay && hoveredDay.dateKey === day.dateKey && (
                                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 pointer-events-none">
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800" />
                                        
                                        {/* Date Header */}
                                        <div className="text-xs text-slate-400 mb-2 text-center font-medium">
                                            {new Date(day.dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </div>
                                        
                                        {/* Stats Grid */}
                                        <div className="space-y-1.5 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Total Bets:</span>
                                                <span className="text-white font-medium">{day.bets}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Won:</span>
                                                <span className="text-emerald-400 font-medium">{day.wins}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Lost:</span>
                                                <span className="text-red-400 font-medium">{day.losses}</span>
                                            </div>
                                            {day.cashedOut > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">Cashed Out:</span>
                                                    <span className="text-blue-400 font-medium">{day.cashedOut}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Win Rate:</span>
                                                <span className="text-white font-medium">{day.winRate.toFixed(1)}%</span>
                                            </div>
                                            <div className="border-t border-slate-700 my-1.5" />
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Staked:</span>
                                                <span className="text-white font-medium">{formatStakedValue(day.staked, day.stakedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Returned:</span>
                                                <span className="text-white font-medium">{formatStakedValue(day.returned, day.returnedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">P/L:</span>
                                                <span className={`font-medium ${day.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {formatPL(day.pl, day.plUnits)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">ROI:</span>
                                                <span className={`font-medium ${day.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {day.roi >= 0 ? '+' : ''}{day.roi.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    {/* Year Navigation */}
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                        <button
                            onClick={goToPrevYear}
                            className="p-1.5 md:p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                        >
                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        
                        <h4 className="text-base md:text-lg font-semibold text-white">{currentYear}</h4>
                        
                        <button
                            onClick={goToNextYear}
                            className="p-1.5 md:p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                        >
                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Monthly Grid */}
                    <div className="grid grid-cols-4 gap-0.5 md:gap-1">
                        {yearlyMonths.map((month, index) => (
                            <div
                                key={index}
                                className={`relative aspect-square rounded-md md:rounded-lg transition-all duration-200 ${getMonthColor(month.pl)} hover:ring-1 hover:ring-slate-500/50 ${month.bets > 0 ? 'cursor-pointer' : ''}`}
                                onMouseEnter={() => month.bets > 0 && setHoveredMonth(month)}
                                onMouseLeave={() => setHoveredMonth(null)}
                            >
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-1 overflow-hidden">
                                    <span className={`text-xs md:text-sm font-medium leading-tight ${month.bets > 0 ? 'text-white' : 'text-slate-500'}`}>
                                        {month.name}
                                    </span>
                                    {month.pl !== undefined && (
                                        <span className={`text-[10px] md:text-xs font-semibold leading-tight truncate max-w-full ${month.pl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                                            {formatPL(month.pl, month.plUnits)}
                                        </span>
                                    )}
                                </div>

                                {/* Hover Tooltip for Month */}
                                {hoveredMonth && hoveredMonth.monthKey === month.monthKey && (
                                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 pointer-events-none">
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800" />
                                        
                                        {/* Month Header */}
                                        <div className="text-xs text-slate-400 mb-2 text-center font-medium">
                                            {month.name} {currentYear}
                                        </div>
                                        
                                        {/* Stats Grid */}
                                        <div className="space-y-1.5 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Total Bets:</span>
                                                <span className="text-white font-medium">{month.bets}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Won:</span>
                                                <span className="text-emerald-400 font-medium">{month.wins}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Lost:</span>
                                                <span className="text-red-400 font-medium">{month.losses}</span>
                                            </div>
                                            {month.cashedOut > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">Cashed Out:</span>
                                                    <span className="text-blue-400 font-medium">{month.cashedOut}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Win Rate:</span>
                                                <span className="text-white font-medium">{month.winRate.toFixed(1)}%</span>
                                            </div>
                                            <div className="border-t border-slate-700 my-1.5" />
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Staked:</span>
                                                <span className="text-white font-medium">{formatStakedValue(month.staked, month.stakedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">Returned:</span>
                                                <span className="text-white font-medium">{formatStakedValue(month.returned, month.returnedUnits)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">P/L:</span>
                                                <span className={`font-medium ${month.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {formatPL(month.pl, month.plUnits)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-400">ROI:</span>
                                                <span className={`font-medium ${month.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {month.roi >= 0 ? '+' : ''}{month.roi.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-slate-700/50">
            {/* Row 1: Bets breakdown */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3">
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">Total Bets</div>
                    <div className="text-base md:text-lg font-bold text-white">{currentStats.totalBets}</div>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">W / L / CO</div>
                    <div className="text-base md:text-lg font-bold">
                        <span className="text-emerald-400">{currentStats.wins}</span>
                        <span className="text-slate-500 mx-0.5 md:mx-1">/</span>
                        <span className="text-red-400">{currentStats.losses}</span>
                        <span className="text-slate-500 mx-0.5 md:mx-1">/</span>
                        <span className="text-blue-400">{currentStats.cashedOut}</span>
                    </div>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">Win Rate</div>
                    <div className="text-base md:text-lg font-bold text-white">
                        {currentStats.winRate.toFixed(1)}%
                    </div>
                </div>
            </div>
            {/* Row 2: Performance metrics - 2x2 on mobile, 4 cols on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">Staked</div>
                    <div className="text-base md:text-lg font-bold text-white truncate">{formatStakedValue(currentStats.staked, currentStats.stakedUnits)}</div>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">Returned</div>
                    <div className="text-base md:text-lg font-bold text-white truncate">{formatStakedValue(currentStats.returned, currentStats.returnedUnits)}</div>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">P/L</div>
                    <div className={`text-base md:text-lg font-bold truncate ${currentStats.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatPL(currentStats.pl, currentStats.plUnits)}
                    </div>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-2.5 md:p-3 text-center">
                    <div className="text-xs md:text-xs text-slate-400 mb-1">ROI</div>
                    <div className={`text-base md:text-lg font-bold ${currentStats.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {currentStats.roi >= 0 ? '+' : ''}{currentStats.roi.toFixed(1)}%
                    </div>
                </div>
            </div>
        </div>

            {/* Legend */}
            <div className="mt-3 flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                        <div className="w-3 h-3 rounded bg-emerald-500/20" />
                        <div className="w-3 h-3 rounded bg-emerald-500/40" />
                        <div className="w-3 h-3 rounded bg-emerald-500/60" />
                    </div>
                    <span className="text-xs text-slate-500">Profit</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                        <div className="w-3 h-3 rounded bg-red-500/20" />
                        <div className="w-3 h-3 rounded bg-red-500/40" />
                        <div className="w-3 h-3 rounded bg-red-500/60" />
                    </div>
                    <span className="text-xs text-slate-500">Loss</span>
                </div>
            </div>
        </div>
    );
}

export default MonthlyCalendar;
